'use client'

import Image from 'next/image'
import { useState } from 'react'

import { motion } from 'framer-motion'

import type { Card } from '@/lib/api-connector-strapi-cms/api/reeve-landing-page/reeveLandingPageApi.types'
import { Markdown } from '@/lib/ui-kit/components/Markdown.component'

interface HowWeHelpCardProps {
  card: Card
  apiAssetsUrl?: string
}

const iconBackgroundColors = {
  yellow: 'bg-reeve-yellow',
  green: 'bg-reeve-green',
  blue: 'bg-reeve-blue',
  purple: 'bg-reeve-purple',
  orange: 'bg-reeve-orange',
}

export const HowWeHelpCard = ({ card, apiAssetsUrl }: HowWeHelpCardProps) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div className="h-full">
      <motion.div
        className="group relative hidden h-full overflow-hidden rounded-lg border border-reeve-divider bg-white shadow-sm xl:block"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <div className="p-4 pt-4">
          <div className="bg-gray-100 relative overflow-hidden rounded-lg xl:h-72 2xl:h-96">
            <div className="absolute inset-x-0 bottom-0 top-0 rounded-lg">
              <motion.div
                className="w-full overflow-hidden rounded-lg"
                style={{
                  height: '100%',
                  transformOrigin: 'top',
                }}
                animate={
                  isHovered
                    ? {
                        height: '80%',
                        y: '0%',
                      }
                    : {
                        height: '100%',
                        y: '0%',
                      }
                }
                transition={{
                  duration: 0.3,
                  ease: [0.2, 0.65, 0.3, 0.9],
                }}
              >
                <div className="h-full w-full p-0.5">
                  <div className="h-full w-full overflow-hidden rounded-lg">
                    <Image
                      className="h-full w-full object-cover object-center"
                      src={`${apiAssetsUrl || ''}${card.image.url}`}
                      width={card.image.width}
                      height={card.image.height}
                      alt={
                        card.image.alternativeText || `${card.heading} image`
                      }
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        <motion.div
          className="p-8"
          animate={{
            opacity: isHovered ? 0 : 1,
          }}
          transition={{
            duration: isHovered ? 0.01 : 0.25,
            ease: 'easeOut',
          }}
        >
          <div
            className={`${iconBackgroundColors[card.iconBackgroundColor]} mb-6 inline-block rounded-full p-2.5`}
          >
            <Image
              className="h-9 w-9"
              src={`${apiAssetsUrl || ''}${card.icon.url}`}
              width={card.icon.width}
              height={card.icon.height}
              alt={card.icon.alternativeText || `${card.heading} icon`}
            />
          </div>
          <div className="text-[1.375rem] font-semibold leading-[1.75rem] text-reeve-primary">
            <Markdown>{card.heading}</Markdown>
          </div>
        </motion.div>

        <motion.div
          className="pointer-events-none absolute inset-0 flex flex-col justify-between rounded-lg bg-white/85 p-10"
          initial={{ y: '100%', opacity: 0 }}
          animate={
            isHovered
              ? { y: 0, opacity: 1, pointerEvents: 'auto' as const }
              : { y: '100%', opacity: 0, pointerEvents: 'none' as const }
          }
          transition={{
            duration: 0.4,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
        >
          <div>
            <motion.div
              className="mb-8 inline-block rounded-full p-2.5"
              style={{
                backgroundColor: isHovered ? 'white' : undefined,
              }}
              animate={{
                backgroundColor: isHovered ? '#ffffff' : undefined,
              }}
              transition={{
                duration: 0.2,
                ease: 'easeInOut',
              }}
              initial={{ scale: 1 }}
            >
              <motion.div
                animate={
                  isHovered ? { rotate: [0, 10, -10, 5, -5, 0] } : { rotate: 0 }
                }
                transition={{
                  duration: 0.3,
                  ease: 'easeInOut',
                  delay: 0.05,
                }}
              >
                <motion.div>
                  <Image
                    className="h-9 w-9"
                    src={`${apiAssetsUrl || ''}${card.icon.url}`}
                    width={card.icon.width}
                    height={card.icon.height}
                    alt={card.icon.alternativeText || `${card.heading} icon`}
                  />
                </motion.div>
              </motion.div>
            </motion.div>
            <div className="text-[1.375rem] font-semibold leading-[1.75rem] text-reeve-primary">
              <Markdown>{card.heading}</Markdown>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isHovered ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{
              duration: 0.3,
              ease: 'easeOut',
              delay: 0,
            }}
          >
            <div className="text-[1.25rem] font-normal leading-[1.75rem] text-reeve-secondary-gray">
              <Markdown>{card.subHeading}</Markdown>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="absolute inset-0 -z-10 rounded-lg bg-gradient-to-br from-transparent to-transparent"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={
            isHovered
              ? {
                  opacity: 0.07,
                  scale: 1.02,
                  background: `linear-gradient(135deg, ${getGradientColor(card.iconBackgroundColor)}, transparent)`,
                }
              : { opacity: 0, scale: 0.95 }
          }
          transition={{
            duration: 0.4,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
        />

        <motion.div
          className="pointer-events-none absolute inset-0 rounded-lg bg-white"
          initial={{ opacity: 0 }}
          animate={isHovered ? { opacity: [0, 0.2, 0] } : { opacity: 0 }}
          transition={{
            duration: isHovered ? 0.3 : 0.35,
            times: [0, 0.1, 1],
            ease: isHovered ? 'easeOut' : [0.25, 0.46, 0.45, 0.94],
          }}
        />
      </motion.div>

      <div className="group relative h-full max-w-2xl overflow-hidden rounded-lg border border-reeve-divider bg-white shadow-sm xl:hidden">
        <div className="p-4 pt-4">
          <div className="bg-gray-100 relative h-72 overflow-hidden rounded-lg">
            <Image
              className="h-full w-full object-cover object-center"
              src={`${apiAssetsUrl || ''}${card.image.url}`}
              width={card.image.width}
              height={card.image.height}
              alt={card.image.alternativeText || `${card.heading} image`}
            />
          </div>
        </div>
        <div className="p-8">
          <div
            className={`${
              iconBackgroundColors[card.iconBackgroundColor]
            } mb-1 inline-block rounded-full p-1.5 xl:p-2.5`}
          >
            <Image
              className="h-7 w-7 xl:h-9 xl:w-9"
              src={`${apiAssetsUrl || ''}${card.icon.url}`}
              width={card.icon.width}
              height={card.icon.height}
              alt={card.icon.alternativeText || `${card.heading} icon`}
            />
          </div>
          <div className="mb-4 text-[1.375rem] font-semibold leading-[1.75rem] text-reeve-primary">
            <Markdown>{card.heading}</Markdown>
          </div>
          <div className="text-[1.125rem] font-normal leading-[1.5rem] text-reeve-secondary-gray">
            <Markdown>{card.subHeading}</Markdown>
          </div>
        </div>
      </div>
    </div>
  )
}

function getGradientColor(color: string) {
  const colorMap = {
    yellow: 'rgb(253, 224, 71)',
    green: 'rgb(74, 222, 128)',
    blue: 'rgb(96, 165, 250)',
    purple: 'rgb(192, 132, 252)',
    orange: 'rgb(251, 146, 60)',
  }
  // @ts-ignore
  return colorMap[color] || colorMap.blue
}
